import { useState } from "react";
import { apiPublic, setStaffSession, getStaffInfo, clearStaffSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import CrmPortal from "@/pages/portals/CrmPortal";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function CrmEntry() {
  const [info, setInfo] = useState(getStaffInfo());
  if (info && info.role === "manager") return <CrmPortal info={info} onLogout={() => { clearStaffSession(); setInfo(null); }} />;
  return <Login expectedRole="manager" onAuthed={setInfo} />;
}

function Login({ expectedRole, onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function login() {
    setBusy(true);
    try {
      const res = await apiPublic.post("/auth/login", { email, password });
      const role = res.data.role;
      // CRM page wants managers; dispatch page wants manager or dispatcher
      if (expectedRole === "manager" && role !== "manager") {
        toast.error(`This account is a "${role}" — use the Dispatch portal instead.`);
        return;
      }
      if (expectedRole === "dispatch" && role !== "manager" && role !== "dispatcher") {
        toast.error(`This account (${role}) is not allowed here.`);
        return;
      }
      setStaffSession(res.data);
      onAuthed(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] grid place-items-center px-5">
      <div className="absolute top-5 left-5">
        <Link to="/" className="text-sm text-[#002060]/70 hover:text-[#FF6600] flex items-center gap-1.5" data-testid="staff-back-home">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </div>
      <div className="w-full max-w-md bg-[#FFFFFF] border-t-4 border-[#FF6600] border border-[#002060]/15 rounded-2xl p-7 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <img src="/brand/logo.png" alt="Space Appliance" className="h-10 w-10 object-contain" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF6600] font-semibold">Space Appliance · Staff</div>
            <h1 className="display text-2xl font-semibold tracking-tight text-[#002060]">
              {expectedRole === "manager" ? "Manager CRM" : "Dispatcher Portal"}
            </h1>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-sm text-[#002060]">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email && password && !busy && login()}
              placeholder="you@gmail.com"
              className="mt-1.5 border-[#002060]/20 focus-visible:ring-[#FF6600]"
            />
          </div>
          <div>
            <Label className="text-sm text-[#002060]">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email && password && !busy && login()}
              placeholder="••••••••"
              className="mt-1.5 border-[#002060]/20 focus-visible:ring-[#FF6600]"
            />
          </div>
          <Button
            data-testid="login-submit"
            disabled={!email || !password || busy}
            onClick={login}
            className="w-full bg-[#FF6600] hover:bg-[#FF6600]/90 rounded-full h-11 text-[#FFFFFF] font-bold uppercase tracking-widest"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </div>

        <p className="mt-6 text-xs text-[#002060]/60">
          Use the email &amp; password your manager gave you. Trouble signing in? Ask your manager to reset it in the CRM.
        </p>
      </div>
    </div>
  );
}

export { Login };
